import React, { useState } from 'react';
import {
  FiChevronLeft,
  FiHome,
  FiMapPin,
  FiPhone,
  FiTag,
  FiUser
} from 'react-icons/fi';
import { IoCalendarClearOutline } from 'react-icons/io5';
import { LuBrickWall } from 'react-icons/lu';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import EmailInitiatedTable from './EmailInitiatedTable';
import RecipientProfile from './RecipientProfile';
import RecipientsViewTopCards from './RecipientsViewTopCards';

// Type casting for icons
const FiTagComponent = FiTag as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const LuBrickWallComponent = LuBrickWall as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiMapPinComponent = FiMapPin as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiHomeComponent = FiHome as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
// const FiEdit2Component = FiEdit2 as unknown as React.ComponentType<{
//   size?: number | string;
//   className?: string;
// }>;
const FiUserComponent = FiUser as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const IoCalendarClearOutlineComponent =
  IoCalendarClearOutline as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
  }>;
// const FiArrowLeftComponent = FiArrowLeft as unknown as React.ComponentType<{
//   size?: number | string;
//   className?: string;
// }>;
const FiChevronLeftComponent = FiChevronLeft as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiPhoneComponent = FiPhone as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

// Main component for viewing recipient details
const ReportsViewRecipient: React.FC = () => {
  // const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // const [isEditing, setIsEditing] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  const location = useLocation();
  const { recipient } = location.state || {};


  // Handle case where no recipient data is found
  if (!recipient) {
    return (
      <div className='p-2'>
        <button
          onClick={() => navigate(-1)}
          className='text-orange-500 flex items-center gap-2'
        >
          <FiChevronLeftComponent /> Back
        </button>
        <p className='mt-4 text-red-500'>Recipient not found.</p>
      </div>
    );
  }

  // Prepare data for top cards
  const topCardsData = {
    openRate: { value: recipient.openRate, change: '' },
    clickRate: { value: recipient.clickRate, change: '' },
    lastOpened: { value: recipient.lastOpened },
    lastClicked: { value: recipient.lastClicked },
    emailsReceived: { value: recipient.emailsReceived },
    emailsOpened: { value: recipient.emailsOpened },
    emailsClicked: { value: recipient.emailsClicked },
    lastActive: { value: recipient.lastActive },
    engagementScore: { value: recipient.engagementScore },
    unsubscribed: { value: recipient.unsubscribed },
    dob: { value: recipient.dob },
    phone: { value: recipient.phone },
    gender: { value: recipient.gender },
    postalCode: { value: recipient.postalCode },
    address: { value: recipient.address },
  };

  return (
    <div className='p-2'>
      {/* Back button */}
      <button
        onClick={() =>
          showProfile ? setShowProfile(false) : navigate(`/reports`)
        }
        className='flex items-center text-sm text-teal-600 mb-4 hover:underline font-semibold'
        style={{ color: '#00899E', fontSize: '14px' }}
      >
        <FiChevronLeftComponent className='mr-1' />{' '}
        {showProfile ? 'Back to Recipient Details' : 'Back to Reports'}
      </button>

      {/* Header */}
      <div>
        <h2 className='text-[20px] font-bold text-[#3F5265] mb-2'>
          {showProfile ? 'Recipient Profile' : 'Recipient Details'}
        </h2>
        <p className='text-[#657584] mb-6 text-[16px]'>
          View key information and engagement insights about this audience
          member.
        </p>
      </div>
      {showProfile ? (
        <RecipientProfile recipient={recipient} />
      ) : (
        <>
          {/* Recipient details section */}
          <div className='bg-white rounded-lg border border-gray-200 shadow-sm mt-6 p-6'>
            <div className='flex items-start justify-between'>
              <div
                className='relative flex items-center gap-4 hover:text-teal-600 hover:underline cursor-pointer group'
                onClick={() => setShowProfile(true)}
              >
                <div className='w-12 h-12 rounded-full bg-purple-200 flex items-center justify-center text-xl font-bold text-purple-700'>
                  {recipient.name[0]}
                </div>
                <div>
                  <h3 className='text-[18px] font-semibold text-[#3F5265]'>
                    {recipient.name}
                  </h3>
                  <p className='text-[#657584] text-[14px]'>
                    {recipient.email}
                  </p>
                </div>
                {/* Tooltip for profile view */}
                <span className='absolute -top-8 left-0 scale-0 group-hover:scale-100 transition-all rounded bg-gray-800 px-2 py-1 text-xs text-white'>
                  View Profile
                </span>
              </div>
            </div>

            <hr className='my-6 border-t' />

            {/* Recipient metadata */}
            <div className='grid grid-cols-2 md:grid-cols-2 gap-4 mt-6 text-sm text-gray-700'>
              <div>
                <ul className='space-y-4 text-gray-600'>
                  <li className='flex items-center'>
                    <span className='flex items-center gap-2 text-xs text-gray-400 mr-3'>
                      <span className='flex items-center justify-center mr-1 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'>
                        <FiPhoneComponent size={17} />
                      </span>
                      PHONE NUMBER
                    </span>
                    <span className='-mt-0.5'>{recipient.phone || '-'}</span>
                  </li>
                  <li className='flex items-center'>
                    <span className='flex items-center gap-2 text-xs text-gray-400 mr-3'>
                      <span className='flex items-center justify-center mr-1 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'>
                        <IoCalendarClearOutlineComponent size={17} />
                      </span>
                      DATE OF BIRTH
                    </span>
                    <span className='-mt-0.5'>{recipient.dob || '-'}</span>
                  </li>
                  <li className='flex items-center'>
                    <span className='flex items-center gap-2 text-xs text-gray-400 mr-3'>
                      <span className='flex items-center justify-center mr-1 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'>
                        <FiUserComponent size={17} />
                      </span>
                      GENDER
                    </span>
                    <span className='-mt-0.5'>{recipient.gender || '-'}</span>
                  </li>
                  <li className='flex items-center'>
                    <span className='flex items-center gap-2 text-xs text-gray-400 mr-3'>
                      <span className='flex items-center justify-center mr-1 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'>
                        <FiHomeComponent size={17} />
                      </span>
                      ADDRESS
                    </span>
                    <span className='-mt-0.5'>{recipient.address || '-'}</span>
                  </li>
                </ul>
              </div>
              <div>
                <ul className='space-y-4 text-gray-600'>
                  <li className='flex items-center'>
                    <span className='flex items-center gap-2 text-xs text-gray-400 mr-3'>
                      <span className='flex items-center justify-center mr-1 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'>
                        <FiMapPinComponent size={17} />
                      </span>
                      POSTAL CODE
                    </span>
                    <span className='-mt-0.5'>
                      {recipient.postalCode || '-'}
                    </span>
                  </li>
                  <li className='flex items-center'>
                    <span className='flex items-center gap-2 text-xs text-gray-400 mr-3'>
                      <span className='flex items-center justify-center mr-1 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'>
                        <LuBrickWallComponent size={17} />
                      </span>
                      SEGMENTS
                    </span>
                    <span className='-mt-0.5'>{recipient.segment || '-'}</span>
                  </li>
                  <li className='flex items-center'>
                    <span className='flex items-center gap-2 text-xs text-gray-400 mr-3'>
                      <span className='flex items-center justify-center mr-1 w-[30px] h-[30px] bg-[#F5F8FA] rounded-[5px] text-gray-400'>
                        <FiTagComponent size={17} />
                      </span>
                      TAGS
                    </span>
                    <span className='-mt-0.5'>
                      {recipient.tags?.join(', ') || '-'}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <RecipientsViewTopCards data={topCardsData} />
        </>
      )}

      {/* Email initiated table */}
      <EmailInitiatedTable
        records={[
          {
            campaign: 'Email Title',
            fromEmail: 'example@email.com',
            sentAt: '15-08-2025',
            status: 'Delivered',
          },
          {
            campaign: 'Email Title',
            fromEmail: 'example@email.com',
            sentAt: '15-08-2025',
            status: 'Not Delivered',
          },
          {
            campaign: 'Email Title',
            fromEmail: 'example@email.com',
            sentAt: '24-08-2025',
            status: 'Bounced',
          },
          {
            campaign: 'Email Title',
            fromEmail: 'example@email.com',
            sentAt: '24-08-2025',
            status: 'Delivered',
          },
          {
            campaign: 'Email Title',
            fromEmail: 'example@email.com',
            sentAt: '24-08-2025',
            status: 'Delivered',
          },
          {
            campaign: 'Email Title',
            fromEmail: 'example@email.com',
            sentAt: '24-08-2025',
            status: 'Delivered',
          },
          {
            campaign: 'Email Title',
            fromEmail: 'example@email.com',
            sentAt: '24-08-2025',
            status: 'Delivered',
          },
        ]}
      />
      <ToastContainer position='top-right' autoClose={3000} />
    </div>
  );
};

export default ReportsViewRecipient;
